import { useEffect, useState } from "react";

import {
  Button,
  RadioButton,
  TextArea,
  Table,
  type TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { CommitteeDateRangePicker } from "./components";
import { DEFAULT_INSTITUTION_COMMITTEE } from "./constants";
import {
  generatingEditedEmployee,
  getCommitteeEmployeeDisplayName,
  setupCommitteeEmployees,
  updateCommitteeEmployee,
  replaceEmployeeInTableData,
} from "./utils";
import {
  getInstitutionCommitteeById,
  saveInstitutionCommittee,
} from "../../api/Committee/InstitutionCommitteeApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  FormCard,
  ToolbarEdit,
} from "../../components/";
import { useEnumCodeBooks } from "../../hooks";
import type { ValidationFailure } from "../../models";
import type {
  CommitteeEmployee,
  InstitutionCommittee,
} from "../../models/Committee";
import { CommitteeType, CommitteeTypeEnum } from "../../models/Committee";
import type { SaveResult } from "../../models/Shared";
import { committeeApprovalDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils } from "../../utils";
import { CommitteeModal } from "./components/CommitteeModal";

export function CommitteeApprovalEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [item, setItem] = useState<InstitutionCommittee>(
    DEFAULT_INSTITUTION_COMMITTEE
  );
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole.name}</div>;
        },
      },
      {
        headerClassName: "col-6",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "edit",
        header: t("common.edit"),
        accessor: (employee) => {
          return (
            <div className={"text-right"}>
              {employee.committeeRole.committeeType !==
                CommitteeTypeEnum.Institution && (
                <Button
                  size="sm"
                  outline={true}
                  onClick={() => handleDetailsClicked(employee)}
                >
                  {t("common.edit")}
                </Button>
              )}
            </div>
          );
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "president",
        header: t("common.expertCommission.president"),
        accessor: (emp: CommitteeEmployee) => {
          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.committeeRole.id}
                name={"item.president"}
                checked={emp.isPresident}
                value={emp.committeeRole.id}
                disabled
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();

  useEffect(() => {
    (async () => {
      const committeeId = Number(id);

      if (committeeId) {
        const result = await getInstitutionCommitteeById(committeeId);

        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
    })();

    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  const handleDateChange = (date: Date, field: "dateFrom" | "dateTo") => {
    const newItem = { ...item, [field]: date };
    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handleDetailsClicked = (
    expertCommissionEmployee: CommitteeEmployee
  ) => {
    setEditing(expertCommissionEmployee);
  };

  const handleOnCloseModal = () => {
    setEditing(null);
  };

  const handleOnSaveCommitteeEmployee = (
    newCommitteeEmployee: CommitteeEmployee
  ) => {
    if (!editing) return;

    const employeeToEdit = generatingEditedEmployee(
      editing,
      newCommitteeEmployee
    );

    setTableData((prev) => replaceEmployeeInTableData(prev, employeeToEdit));
    setItem((currItem) => updateCommitteeEmployee(currItem, employeeToEdit));

    dispatch(setIsEdited());
    setEditing(null);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("common.expertCommission.pageNameEdit")}
        errors={errors}
      />
      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.institution")}
            </Typography>
          </Col>
          <Col md={10}>
            <Typography variant="base" color="light">
              {item.institution?.code} - {item.institution?.name}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>
      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              maxLength={5000}
              disabled
            />
          </Col>
        </Row>
        <Row>
          <CommitteeDateRangePicker
            dateFrom={item.dateFrom ? new Date(item.dateFrom) : null}
            dateTo={item.dateTo ? new Date(item.dateTo) : null}
            onDateFromChange={(date) => handleDateChange(date, "dateFrom")}
            onDateToChange={(date) => handleDateChange(date, "dateTo")}
            errors={errors}
          />
          <Col md={6}></Col>
        </Row>
      </FormCard>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <ToolbarEdit
        showSave={"show"}
        showCancel={"show"}
        handleSave={async () => saveInstitutionCommittee(item)}
        redirectUrlSaveOK={committeeApprovalDetailUrl}
        setSaveErrors={setErrors}
        onSaveSuccess={(data: SaveResult<InstitutionCommittee>) => {
          if (!data.result) return;

          setItem(data.result);
        }}
      />
      <CommitteeModal
        committeeType={CommitteeType.Institution}
        editingEmployee={editing ?? null}
        onSave={handleOnSaveCommitteeEmployee}
        onClose={handleOnCloseModal}
      />
    </ContentArea>
  );
}
